import React from 'react';
import { useMutation } from '@tanstack/react-query';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { useAccountRecord } from './AccountRecordContext';
import { ACCOUNT_CARDS } from './accountModel';

/**
 * AR-380 — the confirmations. Cancel with unsaved changes asks first, and
 * clearing memory or deactivating a member says what is lost and what stays.
 * The scrim closes the dialog, and so does the ×.
 */

export const AccountDialog: React.FC<{
  title: string;
  sub?: string;
  onClose: () => void;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ title, sub, onClose, actions, children }) => (
  /* Radix owns the modal behaviour a hand-rolled scrim does not give you: focus
     moves into the dialog and is contained there, Escape closes it, focus
     returns to whatever opened it, and the rest of the page is inert to a
     screen reader. */
  <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
    <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-lg overflow-y-auto border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1A2021]">
      <DialogHeader>
        <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </DialogTitle>
        {sub ? (
          <DialogDescription className="text-[13px] text-gray-500 dark:text-gray-400">
            {sub}
          </DialogDescription>
        ) : null}
      </DialogHeader>
      {children}
      {actions ? <div className="mt-4 flex flex-wrap gap-2">{actions}</div> : null}
    </DialogContent>
  </Dialog>
);

const PrimaryAction: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="rounded-md bg-orange-700 px-4 py-1.5 text-sm font-medium text-white hover:bg-orange-800 disabled:cursor-not-allowed disabled:opacity-60"
  >
    {children}
  </button>
);

const SecondaryAction: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="rounded-md border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
  >
    {children}
  </button>
);

/** Every card's display name, so the prompt names the card the reader is on. */
const CARD_NAMES: Partial<Record<keyof typeof ACCOUNT_CARDS, string>> = {
  you: 'Personal information',
  addr: 'Mailing address',
  billaddr: 'Billing address',
  bill: 'Payment method',
  biz: 'Business details',
  brand: 'Brand',
  pub: 'Advisor bio',
  spec: 'Specialties + expertise',
  cta: 'Public contact + booking',
  teamset: 'Data access',
  teamdef: 'Team defaults',
  crm: 'Workflow defaults',
  propc: 'Default content',
  propt: 'Terms',
  propx: 'Client experience',
  vault: 'Communication',
  web: 'WorldVia Sites',
  voice: 'AI writing profile',
};

export const AccountConfirmations: React.FC = () => {
  const {
    confirm,
    setConfirm,
    record,
    saveCard,
    discardCard,
    saveAll,
    discardAll,
    setMemberStatus,
    setLive,
  } = useAccountRecord();

  // AR-278 — clearing memory resets the member's AIVIA memory on the server
  // (knowledge graph + cached chat summaries). "Last cleared" is recorded only
  // once that succeeds.
  const clearMemory = useMutation({
    mutationFn: () => apiRequest('DELETE', '/api/knowledge-graph/me/memory'),
    onSuccess: () => {
      setLive(
        'ai.cleared',
        new Date().toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
      );
      setConfirm(null);
      toast({ title: 'AIVIA memory cleared' });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Could not clear AIVIA memory',
        description:
          error instanceof Error ? error.message : 'Please try again in a moment.',
        variant: 'destructive',
      });
    },
  });

  if (!confirm) return null;
  const close = () => setConfirm(null);

  if (confirm.kind === 'unsaved') {
    const name = CARD_NAMES[confirm.card] ?? 'this card';
    return (
      <AccountDialog
        title="Unsaved changes"
        onClose={close}
        actions={
          <>
            <PrimaryAction onClick={() => saveCard(confirm.card)}>Save</PrimaryAction>
            <SecondaryAction onClick={() => discardCard(confirm.card)}>
              Discard
            </SecondaryAction>
            <SecondaryAction onClick={close}>Keep editing</SecondaryAction>
          </>
        }
      >
        <p className="mt-3 text-[14.5px] leading-relaxed text-gray-700 dark:text-gray-300">
          You have not saved your changes to {name}.
        </p>
      </AccountDialog>
    );
  }

  if (confirm.kind === 'unsavedAll') {
    return (
      <AccountDialog
        title="Unsaved changes"
        onClose={close}
        actions={
          <>
            <PrimaryAction onClick={saveAll}>Save</PrimaryAction>
            <SecondaryAction onClick={discardAll}>Discard</SecondaryAction>
            <SecondaryAction onClick={close}>Keep editing</SecondaryAction>
          </>
        }
      >
        <p className="mt-3 text-[14.5px] leading-relaxed text-gray-700 dark:text-gray-300">
          You have not saved your changes on this page.
        </p>
      </AccountDialog>
    );
  }

  if (confirm.kind === 'memory') {
    // The DELETE cannot be cancelled, so the dialog stays open until it settles.
    const closeUnlessPending = () => {
      if (!clearMemory.isPending) close();
    };
    return (
      <AccountDialog
        title="Clear all memory"
        onClose={closeUnlessPending}
        actions={
          <>
            <PrimaryAction
              onClick={() => clearMemory.mutate()}
              disabled={clearMemory.isPending}
            >
              {clearMemory.isPending ? 'Clearing…' : 'Clear all memory'}
            </PrimaryAction>
            <SecondaryAction onClick={close} disabled={clearMemory.isPending}>
              Cancel
            </SecondaryAction>
          </>
        }
      >
        <p className="mt-3 text-[14.5px] leading-relaxed text-gray-700 dark:text-gray-300">
          This clears what AIVIA remembers from your conversations. It does not delete
          clients, trips, proposals, documents or your account.
        </p>
      </AccountDialog>
    );
  }

  // Deactivating a member says what they lose and what stays.
  const member = record.team[confirm.index];
  if (!member) return null;
  return (
    <AccountDialog
      title={`Deactivate ${member.first} ${member.last}?`}
      onClose={close}
      actions={
        <>
          <PrimaryAction onClick={() => setMemberStatus(confirm.index, 'Deactivated', true)}>
            Deactivate
          </PrimaryAction>
          <SecondaryAction onClick={close}>Cancel</SecondaryAction>
        </>
      }
    >
      <p className="mt-3 text-[14.5px] leading-relaxed text-gray-700 dark:text-gray-300">
        They lose access to the platform. Their clients and trips stay with the business.
      </p>
    </AccountDialog>
  );
};
